import type { FunctionComponent, ReactNode } from 'react';
import { useEffect, useState } from 'react';

import { NestedNavPages } from '../../../config/CoreConstants';
import { Logger } from '../../../logger/Logger';
import { useCoreDispatch } from '../../../store/CoreStore';
import { useChatSlice } from '../../../store/slices/chat/selectors';
import { useHeaderSlice } from '../../../store/slices/header/selectors';
import { headerActions } from '../../../store/slices/header/slice';
import { useIsLargerThanWide } from '../../../styles/hooks/media';

import { NavPageBetLimitsConnected } from './betLimits/NavPageBetLimitsConnected';
import { NavPageChatConnected } from './chat/NavPageChatConnected';
import { NavPageHelpConnected } from './help/NavPageHelpConnected';
import { NavPageHistoryConnected } from './history/NavPageHistoryConnected';
import { NavPageNickNameConnected } from './nickName/NavPageNickNameConnected';
import { NavQuickGuideConnected } from './quickGuide/NavQuickGuideConnected';
import { NavPageSettingsConnected } from './settings/NavPageSettingsConnected';


export enum NavPages {
  HOME = 'home', // This is the root drawer page
  CHAT = 'CHAT',
  SETTINGS = 'SETTINGS',
  HISTORY = 'HISTORY',
  BET_LIMITS = 'BET_LIMITS',
  HELP = 'HELP',
  QUICK_GUIDE = 'QUICK_GUIDE',
}

export const NavPagesConnected: FunctionComponent = () => {
  const dispatch = useCoreDispatch();
  const routeLevelLength = useHeaderSlice.getNavRouteLevel();
  const currentNavRoute = useHeaderSlice.getNavRouteByLevel(routeLevelLength);
  const routeByLevel = useHeaderSlice.getNavRouteByLevel(2);
  const [delayedRouteByLevel, setDelayedRouteByLevel] = useState(routeByLevel);
  const isLiveSupportOpen = useChatSlice.getIsLiveSupportOpen();
  const isWide = useIsLargerThanWide();
  useEffect(() => {
    if (routeByLevel) {
      setDelayedRouteByLevel(routeByLevel);
    } else {
      const timeout = window.setTimeout(() => {
        setDelayedRouteByLevel(undefined);
      }, 300);
      return () => window.clearTimeout(timeout);
    }
  }, [routeByLevel]);

  useEffect(() => {
    dispatch(headerActions.setNavForcedExpanded(delayedRouteByLevel === NavPages.QUICK_GUIDE));
  }, [dispatch, delayedRouteByLevel]);

  useEffect(() => {
    if (routeByLevel === NavPages.BET_LIMITS) {
      Logger.logEvent('click', 'chatOpenMobile', Date.now(), 1, isLiveSupportOpen ? 'support' : '', false);
    }
  }, [routeByLevel, isLiveSupportOpen]);

  useEffect(() => {
    if (isWide) {
      dispatch(headerActions.setNavRoute([]));
    }
  }, [isWide, dispatch]);

  return (
    <>
      {((): ReactNode => {
        switch (delayedRouteByLevel) {
          case NavPages.CHAT:
            return <NavPageChatConnected />;
          case NavPages.SETTINGS:
            if (NestedNavPages.SETTINGS_NICKNAME_CHANGE === currentNavRoute) {
              return <NavPageNickNameConnected />;
            }
            return <NavPageSettingsConnected />;
          case NavPages.HISTORY:
            return <NavPageHistoryConnected />;
          case NavPages.BET_LIMITS:
            return <NavPageBetLimitsConnected />;
          case NavPages.HELP:
            return <NavPageHelpConnected />;
          case NavPages.QUICK_GUIDE:
            return <NavQuickGuideConnected />;
          default:
            return null;
        }
      })()}
    </>
  );
};
